import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, ShieldAlert, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddAllergyDialog from './AddAllergyDialog';

const AllergiesTab = () => {
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    fetchAllergies();
  }, []);

  const fetchAllergies = async () => {
    try {
      const response = await API.get('/medical/allergies');
      setAllergies(response.data);
    } catch (error) {
      console.error('Failed to fetch allergies:', error);
      toast.error('Failed to load allergies');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (allergyData) => {
    try {
      await API.post('/medical/allergies', allergyData);
      toast.success('Allergy added');
      fetchAllergies();
      setShowAdd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add allergy');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/medical/allergies/${id}`);
      toast.success('Allergy deleted');
      fetchAllergies();
    } catch (error) {
      toast.error('Failed to delete allergy');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1, 2].map((i) => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  }

  if (allergies.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ShieldAlert className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No allergies or adverse reactions recorded</h3>
          <p className="text-gray-600 mb-4">Central safety list for all allergies</p>
          <Button onClick={() => setShowAdd(true)} className="bg-red-100 hover:bg-red-200 text-red-900">
            <Plus className="h-4 w-4 mr-2" />
            Add Allergy
          </Button>
        </div>
        <AddAllergyDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
      </>
    );
  }

  return (
    <>
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6 flex items-start">
        <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-red-900">Central Safety List</p>
          <p className="text-sm text-red-700">This list includes allergies from all sources: medications, vaccinations, tests, and manual entries.</p>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Allergies & Adverse Reactions</h3>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-red-100 hover:bg-red-200 text-red-900">
          <Plus className="h-4 w-4 mr-2" />
          Add Allergy
        </Button>
      </div>

      <div className="space-y-4">
        {allergies.map((allergy) => (
          <div key={allergy.id} className="border-l-4 border-red-500 bg-red-50 rounded-lg p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">{allergy.substance}</h4>
                <p className="text-sm text-gray-600 mt-1">First noted: {allergy.date_noted}</p>
              </div>
              {allergy.severity && (
                <Badge className={
                  allergy.severity === 'severe' ? 'bg-red-600 text-white' :
                  allergy.severity === 'moderate' ? 'bg-orange-600 text-white' :
                  'bg-yellow-600 text-white'
                }>
                  {allergy.severity}
                </Badge>
              )}
            </div>

            <div className="mb-3">
              <p className="text-sm font-medium text-gray-900">Reaction:</p>
              <p className="text-sm text-gray-700">{allergy.reaction}</p>
            </div>

            {allergy.source_type && (
              <div className="mb-3">
                <Badge variant="outline" className="text-xs">
                  Source: {allergy.source_type}
                </Badge>
              </div>
            )}

            {allergy.notes && (
              <div className="p-3 bg-white rounded-lg mb-3">
                <p className="text-sm text-gray-700">{allergy.notes}</p>
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => handleDelete(allergy.id)} className="text-red-600 hover:text-red-700">
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AddAllergyDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
    </>
  );
};

export default AllergiesTab;